import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { DocumentUploadDrawer } from "@/components/documents/DocumentUploadDrawer";
import { DocumentDetailDrawer } from "@/components/documents/DocumentDetailDrawer";
import { useLocationParticipations } from "@/api/hooks/participations";
import type {
  DocRowStatus,
  DocumentEntityType,
  DocumentType,
  LocationArtifactRow,
} from "@/api/types";

const DOT_COLOR: Record<DocRowStatus, string> = {
  current: "#16a34a",
  expiring: "#eab308",
  expired: "#dc2626",
  in_progress: "#d1d5db",
  missing: "#dc2626",
};

const STATUS_LABEL: Record<DocRowStatus, string> = {
  current: "Current",
  expiring: "Expiring",
  expired: "Expired",
  in_progress: "In progress",
  missing: "Missing",
};

// LocationArtifactType -> DocumentType. The DocumentUpload page uses the
// document_type query param to preselect its dropdown, so we map each
// artifact slug to the matching DocumentType value.
const ARTIFACT_TO_DOC_TYPE: Record<string, DocumentType> = {
  doa: "delegation_log",
  site_capability_questionnaire: "site_capability_questionnaire",
  site_training_log: "site_training_log",
  gcp_training: "gcp_training_cert",
  hipaa_training: "hipaa_training_cert",
};

interface Props {
  rows: LocationArtifactRow[];
  locationId: string;
}

interface UploadTarget {
  documentType: DocumentType;
  entityType: DocumentEntityType;
  entityId: string;
}

export function LocationDocumentsTable({ rows, locationId }: Props) {
  // Read site/DA from the URL so we can invalidate the ClinOps view
  // query after a successful upload — the table refetches and the
  // new document_id flips the row from "Upload" to "View / replace".
  const { id: siteId, da_code: daCode } = useParams<{
    id: string;
    da_code?: string;
  }>();
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  // The delegation-log doc-type needs a Participation context, not the
  // raw Location. When the user clicks Upload on the DOA row we pause
  // here to pick which study (Participation) the log is for.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewDocId, setViewDocId] = useState<string | null>(null);

  const participationsQuery = useLocationParticipations(
    pickerOpen ? locationId : undefined,
  );
  const participations = useMemo(
    () => participationsQuery.data ?? [],
    [participationsQuery.data],
  );

  // If there's exactly one participation at this Location, auto-select
  // it as soon as the picker opens (no point making the operator click).
  useEffect(() => {
    if (!pickerOpen) return;
    if (participations.length !== 1) return;
    const only = participations[0];
    setPickerOpen(false);
    setUploadTarget({
      documentType: "delegation_log",
      entityType: "participation",
      entityId: only.id,
    });
  }, [pickerOpen, participations]);

  const handleUploadClick = (docType: DocumentType) => {
    if (docType === "delegation_log") {
      setPickerOpen(true);
      return;
    }
    setUploadTarget({
      documentType: docType,
      entityType: "location",
      entityId: locationId,
    });
  };

  return (
    <>
      {/* Fixed table-layout + per-column widths keep the column edges
          steady when switching between location tabs — the actions
          column would otherwise jump between "Upload" and "View /
          replace" widths and shift everything else. */}
      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell>Location document</TableCell>
            <TableCell sx={{ width: 160 }}>Status</TableCell>
            <TableCell sx={{ width: 140 }}>Expiration</TableCell>
            <TableCell sx={{ width: 150 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const docType = ARTIFACT_TO_DOC_TYPE[r.artifact_type];
            return (
              <TableRow key={r.artifact_type}>
                <TableCell>{r.label}</TableCell>
                <TableCell>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{
                      width: 9, height: 9, borderRadius: "50%",
                      bgcolor: DOT_COLOR[r.status],
                    }} />
                    {STATUS_LABEL[r.status]}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                  {r.expires_at ?? "—"}
                </TableCell>
                <TableCell align="right">
                  {r.document_id ? (
                    <Button
                      size="small"
                      onClick={() => setViewDocId(r.document_id)}
                    >
                      View / replace
                    </Button>
                  ) : docType ? (
                    <Button size="small" onClick={() => handleUploadClick(docType)}>
                      Upload
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)}>
        <DialogTitle>Which study is this delegation log for?</DialogTitle>
        <DialogContent>
          {participationsQuery.isLoading && (
            <Box sx={{ py: 2 }}>Loading studies…</Box>
          )}
          {!participationsQuery.isLoading && participations.length === 0 && (
            <Alert severity="warning">
              This location isn't enrolled in any studies yet. Enroll the
              location in a pharma study first, then upload the delegation
              log from that participation.
            </Alert>
          )}
          {participations.length > 1 && (
            <List dense>
              {participations.map((p) => (
                <ListItemButton
                  key={p.id}
                  onClick={() => {
                    setPickerOpen(false);
                    setUploadTarget({
                      documentType: "delegation_log",
                      entityType: "participation",
                      entityId: p.id,
                    });
                  }}
                >
                  <ListItemText
                    primary={p.study_name}
                    secondary={`Status: ${p.status} · phase ${p.current_phase}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <DocumentUploadDrawer
        open={uploadTarget !== null}
        onClose={() => setUploadTarget(null)}
        entityType={uploadTarget?.entityType ?? "location"}
        entityId={uploadTarget?.entityId ?? locationId}
        documentType={uploadTarget?.documentType}
        invalidateKeys={[
          ["sites", siteId, "clinops", daCode],
          ["documents"],
        ]}
      />
      <DocumentDetailDrawer
        open={viewDocId !== null}
        onClose={() => setViewDocId(null)}
        documentId={viewDocId}
        invalidateKeys={[
          ["sites", siteId, "clinops", daCode],
          ["documents"],
        ]}
      />
    </>
  );
}
